import React, { useState } from "react";
import { MapContainer, GeoJSON, Marker } from "react-leaflet";
import L from "leaflet";
import distance from "@turf/distance";
import LocationMarker from "./locationMarker";
import ErrorLine from "./ErrorLine";
import NameBox from "./NameBox";
import data from "../N5000ny.json";
import data1200 from "../1200.json";
import data1800 from "../1800.json";

const green = { color: "green", weight: 0, fillOpacity: 1, interactive: false };
const brown = {
  color: "#734C00",
  weight: 0,
  fillOpacity: 1,
  interactive: false,
};
const white = { color: "white", weight: 0, fillOpacity: 1, interactive: false };

// Custom icons for markers
const guessIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.5 12.5 28.5S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z" fill="#3b82f6"/>
      <circle cx="12.5" cy="12.5" r="7" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
});

const actualIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.5 12.5 28.5S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z" fill="#22c55e"/>
      <circle cx="12.5" cy="12.5" r="7" fill="white"/>
      <path d="M9 12.5l2 2 4-4" stroke="#22c55e" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
});

const DailyChallenge = ({ challenge, user, onComplete, onBack }) => {
  const [guess, setGuess] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [distanceAway, setDistanceAway] = useState(0);
  const [score, setScore] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const passGuess = (point) => {
    setGuess(point);
  };

  const getDistance = () => {
    if (!guess || isConfirming) return;

    setIsConfirming(true);

    // Convert guess from [lat, lng] to [lng, lat] for turf
    const guessCoords = [guess[1], guess[0]];

    // Goal coordinates are already [lng, lat]
    const goalCoords = [
      challenge.location.longitude,
      challenge.location.latitude,
    ];

    const dist = distance(guessCoords, goalCoords);
    setDistanceAway(dist);

    // Calculate points: 5000 points if exact, minus 10 points per km away
    const pointsEarned = Math.max(0, Math.round(5000 - dist * 10));
    setScore(pointsEarned);

    // Show confetti for high scores
    if (pointsEarned >= 4000) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }

    setConfirmed(true);
    setIsConfirming(false);
  };

  const handleFinish = () => {
    onComplete(challenge.day, score, guess);
  };

  return (
    <div className="relative w-full h-screen">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-advent-primary-dark to-advent-secondary-dark text-white p-4 shadow-lg">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <button
            onClick={onBack}
            disabled={confirmed}
            className={`px-4 py-2 rounded-lg font-semibold ${
              confirmed
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-white text-gray-800 hover:bg-gray-100"
            }`}
          >
            ← Tilbake
          </button>
          <div className="text-center flex-1">
            <h2 className="text-2xl font-bold">Luke {challenge.day}</h2>
            <p className="text-lg font-semibold text-yellow-300">
              {challenge.locationName}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold">Poeng: {score}</div>
          </div>
        </div>
      </div>

      {/* Confirm/Finish Button */}
      {guess && (
        <div
          className={`absolute z-[9999] ${
            !confirmed
              ? "bottom-8 left-1/2 transform -translate-x-1/2"
              : "top-24 right-8"
          }`}
        >
          {!confirmed ? (
            <button
              onClick={getDistance}
              disabled={isConfirming}
              className={`px-8 py-4 text-white text-xl font-bold rounded-lg shadow-xl transition-all duration-200 transform ${
                isConfirming
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-advent-primary-light hover:bg-advent-primary hover:scale-105"
              }`}
            >
              {isConfirming ? "Beregner..." : "Bekreft svar"}
            </button>
          ) : (
            <div className="bg-white rounded-lg shadow-xl p-6 text-center relative overflow-hidden">
              {showConfetti && (
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(50)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute animate-confetti"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `-10px`,
                        animationDelay: `${Math.random() * 0.5}s`,
                        fontSize: `${Math.random() * 20 + 10}px`,
                      }}
                    >
                      {
                        ["🎉", "⭐", "✨", "🎊", "🏆"][
                          Math.floor(Math.random() * 5)
                        ]
                      }
                    </div>
                  ))}
                </div>
              )}
              <div className="text-2xl font-bold text-gray-800 mb-2">
                📍 {challenge.locationName}
              </div>
              <div className="text-5xl font-bold text-gray-800 mb-2">
                {score} poeng!
              </div>
              {score >= 4000 && (
                <div className="text-xl text-yellow-600 font-bold mb-2">
                  🏆 Fantastisk! 🏆
                </div>
              )}
              <div className="text-lg text-gray-600 mb-4">
                Du var {distanceAway.toFixed(1)} km unna
              </div>
              <button
                onClick={handleFinish}
                className="px-8 py-3 bg-advent-secondary hover:bg-advent-secondary-dark text-white text-xl font-bold rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                Fullfør
              </button>
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <MapContainer
        key={challenge.day}
        center={[62.5873, 9.6707]}
        zoom={6}
        scrollWheelZoom={true}
        minZoom={5}
        maxZoom={12}
        maxBounds={[
          [50.0, -20.0], // Southwest - extends far south and west
          [75.0, 55.0], // Northeast - extends far north and east
        ]}
        maxBoundsViscosity={0.8}
        className="w-full h-full"
      >
        <NameBox name={challenge.locationName} confirmed={confirmed} />
        {!confirmed && <LocationMarker passGuess={passGuess} icon={guessIcon} />}
        <GeoJSON style={green} data={data} />
        <GeoJSON style={brown} data={data1200} />
        <GeoJSON style={white} data={data1800} />
        {confirmed && (
          <Marker
            position={[
              challenge.location.latitude,
              challenge.location.longitude,
            ]}
            icon={actualIcon}
          />
        )}
        {confirmed && <Marker position={guess} icon={guessIcon} />}
        {confirmed && (
          <ErrorLine
            guess={guess}
            actualPoint={[
              challenge.location.latitude,
              challenge.location.longitude,
            ]}
            distanceAway={distanceAway}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default DailyChallenge;
